import type { BookingConfirmationSummary } from '@massilia/queries'

import { Button } from '@massilia/ui'
import { Link } from '@tanstack/react-router'
import { CreditCard } from 'lucide-react'

import { CarRentalsSection } from '@/components/booking-sections/car-rentals-section'
import { ExtrasSection } from '@/components/booking-sections/extras-section'
import { HotelsSection } from '@/components/booking-sections/hotels-section'
import { InsuranceSection } from '@/components/booking-sections/insurance-section'
import { BankDetailsGrid } from '@/components/shared/bank-details-grid'
import { SpecificitySection } from '@/components/specificity/specificity-section'

import type { PaymentConfirmationBookingData } from './payment-confirmation-booking-data'

import { CompleteYourTripSection } from './complete-your-trip-section'
import { PaymentConfirmationLayout } from './payment-confirmation-layout'
import { PaymentConfirmationSharedScreen } from './payment-confirmation-shared-screen'
import { paymentConfirmationVariants } from './payment-confirmation-variants'
import { SummaryRow } from './summary-row'
import { TripRecapSection } from './trip-recap-section'
import { WeekendBookingBanner } from './weekend-booking-banner'

interface PaymentPendingComponentProps {
  sessionId?: string
  booking: PaymentConfirmationBookingData
  summary?: BookingConfirmationSummary
}

export function PaymentPendingComponent({
  sessionId,
  booking,
  summary,
}: PaymentPendingComponentProps) {
  const variant = paymentConfirmationVariants.pending
  const isReunion = summary?.flightSegments[0]?.departureLocation === 'RUN'

  const isInstallment = summary?.installmentChosen ?? false

  return (
    <PaymentConfirmationLayout variant={variant}>
      <WeekendBookingBanner summary={summary} />
      <div className="border-gray-light-200 flex flex-col gap-3 rounded-2xl border bg-white p-6">
        <div className="text-gray-light-900 text-2xl font-semibold">{variant.title}</div>
        <p className="text-gray-light-700 text-sm">{variant.description}</p>

        <SummaryRow label="Numéro de dossier" value={booking.bookingRef} />
        <SummaryRow label="Adresse email" value={booking.customerEmail} />
        <SummaryRow label="Téléphone" value={booking.customerPhone} />
        <SummaryRow label="Tarif" value={booking.totalAmountLabel} />
        {booking.depositDeadlineLabel && (
          <SummaryRow
            label={
              isInstallment
                ? "Date limite de réception de l'acompte"
                : 'Date limite de réception du règlement'
            }
            value={booking.depositDeadlineLabel}
          />
        )}

        <div className="mt-2 flex flex-wrap gap-3">
          <Link to="/login">
            <Button type="button">Accéder à ma réservation</Button>
          </Link>
          <Link to="." href="https://wp.massiliavoyages.fr/conditions-generales-vente-vols-secs/">
            <Button type="button" variant="outline">
              Conditions générales de ventes
            </Button>
          </Link>
        </div>
      </div>

      <div className="border-gray-light-200 flex flex-col gap-3 rounded-2xl border bg-white p-6">
        <div className="text-gray-light-900 text-2xl font-semibold">
          Règlement de votre réservation et modalités de paiements
        </div>

        <SummaryRow label="Reste à payer" value={booking.remainingAmountLabel} />

        <BankDetailsGrid isReunion={isReunion} />

        <div className="mt-1">
          <Button asChild className="bg-brand-600 hover:bg-brand-700">
            <Link
              search={{
                email: booking.customerEmail ?? '',
                dossierNumber: booking.bookingRef,
              }}
              to="/payer-acompte"
            >
              <CreditCard className="mr-2 size-4" />
              Payer un acompte en CB
            </Link>
          </Button>
        </div>
      </div>

      <TripRecapSection summary={summary} />

      {summary?.insurance && <InsuranceSection insurance={summary.insurance} />}
      {summary?.hotels && summary.hotels.length > 0 && <HotelsSection hotels={summary.hotels} />}
      {summary?.carRentals && summary.carRentals.length > 0 && (
        <CarRentalsSection carRentals={summary.carRentals} />
      )}
      {summary?.extras && summary.extras.length > 0 && <ExtrasSection extras={summary.extras} />}

      <CompleteYourTripSection
        bookingId={summary?.bookingId}
        hasInsurance={!!summary?.insurance}
        sessionId={sessionId}
        summary={summary}
      />

      {sessionId && <SpecificitySection sessionId={sessionId} />}

      <PaymentConfirmationSharedScreen
        departureAirportCode={summary?.flightSegments[0]?.departureLocation}
      />
    </PaymentConfirmationLayout>
  )
}
